/**
 * Iter XX.84 — Live cricket breakthroughs (empathetic proof toast).
 *
 * Contract with the backend (/api/breakthroughs):
 *   {
 *     enabled: bool,
 *     messages: { generic:[…], batting:[…], bowling:[…],
 *                 confidence:[…], video:[…] },
 *     chip_category_map: { got_out:"batting", … },
 *     generic_delay_ms, visible_ms, fade_ms, tailored_delay_ms,
 *     config_version: "xx84_v1"
 *   }
 *
 * Founder rules honoured here:
 *   · Never appears more than once per session (guard via
 *     sessionStorage.g_live_breakthrough_shown_v1).
 *   · Truthful, anonymized copy from the server config only —
 *     never fabricates a message client-side.
 *   · Chip-tailored toast is a SEPARATE moment from the generic
 *     toast. Never overlap: `showTailoredIfNotVisible` refuses to
 *     fire while the generic toast is on screen.
 *   · Tailored delay capped at ≤300 ms so the tap feels responsive.
 *   · Exposure recorded via /api/breakthroughs/track so the funnel
 *     query can segment exposed vs non-exposed cohorts for
 *     landing_page_viewed → conversation_started.
 */
import { getToken } from "./nativeTokenStorage";
import { credentialsMode } from "./nativePlatform";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const SHOWN_ONCE_KEY = "g_live_breakthrough_shown_v1";

const DEFAULT_TIMING = {
  generic_delay_ms: 4000,
  visible_ms: 2500,
  fade_ms: 250,
  tailored_delay_ms: 250,
};

let _configCache = null;
let _configPromise = null;

function _browserId() {
  try { return localStorage.getItem("gully_browser_id") || null; }
  catch { return null; }
}

function _authHeaders() {
  const headers = { "Content-Type": "application/json" };
  try {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  } catch { /* noop */ }
  const bid = _browserId();
  if (bid) headers["X-Browser-Id"] = bid;
  return headers;
}

/**
 * Fetch the config from the server. Cached for the lifetime of the
 * SPA — the message pool is small and rotates client-side.
 */
export async function loadConfig() {
  if (_configCache) return _configCache;
  if (_configPromise) return _configPromise;
  if (!API_BASE) return null;
  _configPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/breakthroughs`, {
        method: "GET",
        headers: _authHeaders(),
        credentials: credentialsMode(),
      });
      if (!res.ok) return null;
      const body = await res.json().catch(() => null);
      if (!body) return null;
      _configCache = {
        enabled: !!body.enabled,
        messages: body.messages || {},
        chipCategoryMap: body.chip_category_map || {},
        timing: {
          generic_delay_ms: Number(body.generic_delay_ms) || DEFAULT_TIMING.generic_delay_ms,
          visible_ms: Number(body.visible_ms) || DEFAULT_TIMING.visible_ms,
          fade_ms: Number(body.fade_ms) || DEFAULT_TIMING.fade_ms,
          tailored_delay_ms: Math.min(
            300, Number(body.tailored_delay_ms) || DEFAULT_TIMING.tailored_delay_ms,
          ),
        },
        configVersion: body.config_version || "unknown",
      };
      return _configCache;
    } catch {
      return null;
    } finally {
      _configPromise = null;
    }
  })();
  return _configPromise;
}

export function hasShownThisSession() {
  try { return sessionStorage.getItem(SHOWN_ONCE_KEY) === "1"; }
  catch { return false; }
}

function _markShown() {
  try { sessionStorage.setItem(SHOWN_ONCE_KEY, "1"); }
  catch { /* noop */ }
}

/**
 * Pick a random message from the given category. Falls back to
 * `generic` if the category is empty. Returns null when nothing
 * is available.
 */
export function pickMessage(config, category) {
  if (!config || !config.messages) return null;
  const pool = (config.messages[category] || []).slice();
  const fallback = (config.messages.generic || []).slice();
  const source = pool.length ? pool : fallback;
  if (!source.length) return null;
  const idx = Math.floor(Math.random() * source.length);
  const picked = source[idx];
  return { category, ...picked };
}

/**
 * Pick a category-matched message for a landing chip. If the chip's
 * mapped category has no rows, fall back to `generic`.
 */
export function pickForChip(config, chipId) {
  if (!config) return null;
  const cat = (config.chipCategoryMap || {})[chipId] || "generic";
  return pickMessage(config, cat);
}

/**
 * Fire-and-forget analytics beacon. Records the shown/clicked event
 * server-side so the funnel query can segment exposed cohorts.
 * `page` is the surface that fired it (e.g. "activation_landing").
 */
export async function trackShown({ category, variant, page, sessionId }) {
  if (!API_BASE) return;
  _markShown();
  const bid = _browserId();
  try {
    await fetch(`${API_BASE}/api/breakthroughs/track`, {
      method: "POST",
      headers: _authHeaders(),
      credentials: credentialsMode(),
      body: JSON.stringify({
        event: "live_breakthrough_shown",
        category, variant, page,
        session_id: sessionId || null,
        browser_id: bid,
        exposed: true,
      }),
      keepalive: true,
    });
  } catch { /* non-fatal — analytics only */ }
  // Also mirror to Clarity so the founder can filter recordings.
  try {
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("event", "live_breakthrough_shown");
      window.clarity("set", "breakthrough_category", category);
      window.clarity("set", "breakthrough_variant", variant);
    }
  } catch { /* noop */ }
}

export async function trackClicked({ category, variant, page, sessionId }) {
  if (!API_BASE) return;
  const bid = _browserId();
  try {
    await fetch(`${API_BASE}/api/breakthroughs/track`, {
      method: "POST",
      headers: _authHeaders(),
      credentials: credentialsMode(),
      body: JSON.stringify({
        event: "live_breakthrough_clicked",
        category, variant, page,
        session_id: sessionId || null,
        browser_id: bid,
        exposed: true,
      }),
      keepalive: true,
    });
  } catch { /* non-fatal */ }
  try {
    if (typeof window !== "undefined" && window.clarity) {
      window.clarity("event", "live_breakthrough_clicked");
    }
  } catch { /* noop */ }
}
